import { useTranslation } from 'react-i18next';
import { ConfirmButton } from '@/components/ui';
import type { KeycloakSession } from './profileApi';
import { formatSessionTime } from './sessionFormat';

export interface ProfileSessionsTabProps {
    isLoading: boolean;
    softError: boolean;
    isSuccess: boolean;
    sessions: KeycloakSession[];
    currentSid?: string;
    endingSessionId?: string;
    onEndSession: (sessionId: string) => void;
    onOpenLogoutAllModal: () => void;
    onOpenPasswordModal: () => void;
}

function DeviceIcon() {
    return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <rect x="3" y="4" width="18" height="12" rx="2" />
            <path d="M8 20h8" strokeLinecap="round" />
        </svg>
    );
}

function LogoutIcon() {
    return (
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
            <path d="M15 5V4a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-1" strokeLinecap="round" />
            <path d="M10 12h11m0 0-3-3m3 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function WarnIcon() {
    return (
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
            <path d="M12 4 2.5 20h19L12 4z" strokeLinejoin="round" />
            <path d="M12 10v4M12 17h.01" strokeLinecap="round" />
        </svg>
    );
}

/**
 * `/profile` — "Phiên đăng nhập" tab (frame 79) + the "Đăng xuất mọi thiết bị"
 * confirm (frame 82, rendered by the caller as `LogoutAllDevicesModal`).
 *
 * EAA-1820: restyled into frame 79's table (Thiết bị / Địa chỉ IP / Bắt đầu /
 * Hoạt động gần nhất / action) from the pre-tab per-row-card list EAA-1822
 * shipped. The "Thiết bị" column is the one honest gap: `KeycloakSession`
 * (`profileApi.ts`, pinned to `KeycloakSessionSerializer`) carries
 * `id/ip_address/start/last_access/clients` — no browser or OS string. The
 * frame's "Chrome trên macOS" is sample copy; this renders the real
 * `clients` (which app the session belongs to) as the row's label instead of
 * fabricating a user-agent parse the backend does not send.
 */
export function ProfileSessionsTab({
    isLoading,
    softError,
    isSuccess,
    sessions,
    currentSid,
    endingSessionId,
    onEndSession,
    onOpenLogoutAllModal,
    onOpenPasswordModal,
}: ProfileSessionsTabProps) {
    const { t } = useTranslation();

    return (
        <>
            <div className="portal-profile-notice">
                <InfoIcon />
                <span>
                    {t('profile.sessionsTab.ownSessionsNoticePrefix')}
                    <strong>{t('profile.sessionsTab.ownSessionsNoticeBold')}</strong>
                    {t('profile.sessionsTab.ownSessionsNoticeSuffix')}
                </span>
            </div>

            <div className="portal-profile-card portal-profile-card-flush">
                <div className="portal-profile-sessions-header">
                    <span className="portal-profile-info-card-title">
                        {t('profile.sessionsTab.openSessionsTitle', { count: sessions.length })}
                    </span>
                    <span className="portal-profile-topbar-spacer" />
                    <button
                        type="button"
                        className="portal-btn portal-btn-danger"
                        onClick={onOpenLogoutAllModal}
                        disabled={sessions.length === 0}
                    >
                        <LogoutIcon />
                        {t('profile.logoutAll')}
                    </button>
                </div>

                {isLoading && <p aria-busy="true">{t('profile.sessionsCard.loading')}</p>}
                {softError && (
                    <p className="portal-profile-soft-error">{t('profile.sessionsCard.error')}</p>
                )}
                {isSuccess && sessions.length === 0 && (
                    <p className="portal-profile-empty">{t('profile.sessionsCard.empty')}</p>
                )}

                {sessions.length > 0 && (
                    <div className="portal-profile-sessions-table-wrap">
                        <table className="portal-profile-sessions-table">
                            <thead>
                                <tr>
                                    <th>{t('profile.sessionsTab.deviceHeader')}</th>
                                    <th>{t('profile.sessionsTab.ipHeader')}</th>
                                    <th>{t('profile.sessionsTab.startHeader')}</th>
                                    <th>{t('profile.sessionsTab.lastActiveHeader')}</th>
                                    <th aria-hidden="true" />
                                </tr>
                            </thead>
                            <tbody>
                                {sessions.map((session) => {
                                    const isThisDevice = Boolean(currentSid) && session.id === currentSid;
                                    const ending = endingSessionId === session.id;
                                    return (
                                        <tr
                                            key={session.id}
                                            className={isThisDevice ? 'is-current-device' : undefined}
                                        >
                                            <td>
                                                <span className="portal-profile-device-cell">
                                                    <span className="portal-profile-device-icon">
                                                        <DeviceIcon />
                                                    </span>
                                                    <span className="portal-profile-device-labels">
                                                        <span className="portal-profile-device-name">
                                                            {session.clients && session.clients.length > 0
                                                                ? session.clients.join(', ')
                                                                : t('profile.unknownApp')}
                                                            {isThisDevice && (
                                                                <span className="portal-profile-current-tag">
                                                                    {t('profile.thisDevice')}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </span>
                                                </span>
                                            </td>
                                            <td>
                                                {session.ip_address ? (
                                                    <code>{session.ip_address}</code>
                                                ) : (
                                                    <span className="portal-muted">{t('profile.unknownIp')}</span>
                                                )}
                                            </td>
                                            <td className="portal-profile-session-time-cell">
                                                {formatSessionTime(session.start, t('profile.unknownAccessTime'))}
                                            </td>
                                            <td className="portal-profile-session-time-cell">
                                                {formatSessionTime(
                                                    session.last_access,
                                                    t('profile.unknownAccessTime')
                                                )}
                                            </td>
                                            <td className="portal-profile-session-action-cell">
                                                {isThisDevice ? (
                                                    <span className="portal-muted">—</span>
                                                ) : (
                                                    <ConfirmButton
                                                        label={
                                                            ending
                                                                ? t('profile.endingSession')
                                                                : t('profile.endSession')
                                                        }
                                                        danger
                                                        confirmTitle={t('profile.endSessionConfirmTitle')}
                                                        confirmDescription={t(
                                                            'profile.endSessionConfirmOtherDevice'
                                                        )}
                                                        disabled={Boolean(endingSessionId)}
                                                        onConfirm={() => onEndSession(session.id)}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="portal-profile-sessions-footnote">
                    <WarnIcon />
                    {t('profile.sessionsTab.footerWarning')}
                </div>
            </div>

            <div className="portal-profile-card portal-profile-password-card">
                <span className="portal-profile-info-card-title">
                    {t('profile.infoTab.passwordCardTitle')}
                </span>
                <span className="portal-profile-info-card-desc">
                    {t('profile.sessionsTab.passwordCardDesc')}
                </span>
                <button
                    type="button"
                    className="portal-btn portal-profile-sessions-password-btn"
                    onClick={onOpenPasswordModal}
                >
                    {t('profile.changePassword')}
                </button>
            </div>
        </>
    );
}

function InfoIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
            className="portal-profile-notice-icon"
        >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5M12 8h.01" strokeLinecap="round" />
        </svg>
    );
}

export default ProfileSessionsTab;
